/**
 * Befehlspalette (⌘K bzw. Strg+K).
 *
 * Dritte Darstellung derselben Navigations-Registry. Sie ist bewusst auf
 * **Navigation** beschränkt: Eine Volltextsuche über Analysen und Kunden
 * steht unter „Später" in PLAN.md und braucht eine eigene, mandantensichere
 * Abfrage — die hier vorzutäuschen wäre schlimmer als sie wegzulassen.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import type { UserRole } from '@/types';
import { zieleFuerRolle } from '@/lib/navigation';

export interface CommandPaletteProps {
  /** Rolle des angemeldeten Nutzers — wie bei der Sidebar, rein kosmetisch. */
  rolle?: UserRole;
}

export function CommandPalette({ rolle }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');

  const ziele = useMemo(() => {
    const begriff = suche.trim().toLowerCase();
    const alle = zieleFuerRolle(rolle).map((z) => ({ ...z, label: t(z.labelKey) }));
    return begriff ? alle.filter((z) => z.label.toLowerCase().includes(begriff)) : alle;
  }, [suche, t, rolle]);

  useEffect(() => {
    function beiTaste(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOffen((o) => !o);
      }
      if (e.key === 'Escape') setOffen(false);
    }

    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, []);

  const springe = useCallback(
    (href: string) => {
      setOffen(false);
      setSuche('');
      navigate(href);
    },
    [navigate]
  );

  if (!offen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('common.search')}
      className="fixed inset-0 z-50 flex items-start justify-center bg-surface-900/40 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={() => setOffen(false)}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-surface-300 bg-surface-100 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-surface-200 px-4">
          <Search className="h-4 w-4 text-surface-500" aria-hidden="true" />
          <input
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-liste"
            aria-label={t('common.search')}
            autoFocus
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder={t('common.search')}
            className="w-full bg-transparent py-3 text-sm text-surface-900 outline-none placeholder:text-surface-500"
          />
        </div>

        <ul id="palette-liste" role="listbox" className="max-h-72 overflow-y-auto p-2">
          {ziele.map((ziel) => (
            <li key={ziel.href} role="option" aria-selected="false">
              <button
                type="button"
                onClick={() => springe(ziel.href)}
                className="w-full rounded-lg px-3 py-2 text-left text-sm text-surface-700 hover:bg-surface-200"
              >
                {ziel.label}
              </button>
            </li>
          ))}

          {ziele.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-surface-500">{t('common.empty')}</li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default CommandPalette;
